import React, { useState, useEffect, useCallback } from "react";
import {
  FileText,
  UserCheck,
  Send,
  Lock,
  Unlock,
  AlertTriangle,
  CheckCircle2,
  Clock,
  RotateCcw,
  History,
  Download,
  Share2,
  Trash2,
  ExternalLink,
  Loader2,
  ShieldAlert,
} from "lucide-react";
import apiClient, { getApiErrorMessage, logAppError } from "@/lib/api";
import {
  getCvCycle,
  getCandidateCvCycles,
  getCvCycleInvitations,
  inviteCandidateCvCycle,
} from "@/lib/recruitmentApi";

export default function CvCycleWorkflowPanel({
  candidateId,
  cycleId,
  onCycleUpdated,
  onOpenDraft,
  canWrite = true,
}) {
  const [cycle, setCycle] = useState(null);
  const [invitations, setInvitations] = useState([]);
  const [cyclesHistory, setCyclesHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successNotice, setSuccessNotice] = useState(null);

  // Modals
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteNotes, setInviteNotes] = useState("");
  const [validityHours, setValidityHours] = useState(72);
  const [generatedInviteLink, setGeneratedInviteLink] = useState(null);

  const [showRevokeModal, setShowRevokeModal] = useState(false);
  const [invitationToRevoke, setInvitationToRevoke] = useState(null);
  const [revokeReason, setRevokeReason] = useState("");

  const [showFinalizeModal, setShowFinalizeModal] = useState(false);
  const [finalDocId, setFinalDocId] = useState("");

  const [showReuseModal, setShowReuseModal] = useState(false);
  const [selectedApplicationId, setSelectedApplicationId] = useState("");

  // 1. Fetch cycle data & invitations
  const loadCycleData = useCallback(async () => {
    if (!cycleId && !candidateId) return;
    setIsLoading(true);
    setError(null);

    try {
      let activeCycle = null;

      if (cycleId) {
        activeCycle = await getCvCycle(cycleId);
      } else {
        // Fetch candidate cycles
        const list = await getCandidateCvCycles(candidateId);
        const cycleList = Array.isArray(list) ? list : [];
        setCyclesHistory(cycleList);
        activeCycle = cycleList.find((c) => c.status !== "archived") || cycleList[0] || null;
      }

      setCycle(activeCycle);

      if (activeCycle) {
        // Fetch invitations for this cycle
        try {
          const invList = await getCvCycleInvitations(activeCycle.cycle_id);
          setInvitations(Array.isArray(invList) ? invList : []);
        } catch (invErr) {
          logAppError("CvCycleWorkflowPanel.loadInvitations", invErr);
        }

        // Fetch candidate cycle history (3 latest)
        try {
          const historyList = await getCandidateCvCycles(activeCycle.candidate_id);
          setCyclesHistory(Array.isArray(historyList) ? historyList : []);
        } catch (histErr) {
          logAppError("CvCycleWorkflowPanel.loadHistory", histErr);
        }
      }
    } catch (err) {
      setError(getApiErrorMessage(err, "Impossible de charger le cycle CV"));
      logAppError("CvCycleWorkflowPanel.loadCycleData", err);
    } finally {
      setIsLoading(false);
    }
  }, [cycleId, candidateId]);

  useEffect(() => {
    loadCycleData();
  }, [loadCycleData]);

  // Active invitation & lock calculation
  const latestInvitation = invitations[0] || null;
  const isCandidateLockActive = (() => {
    if (!cycle?.candidate_edit_lock?.locked_until) return false;
    return new Date(cycle.candidate_edit_lock.locked_until).getTime() > Date.now();
  })();

  const isInvitationActive = (() => {
    if (!latestInvitation) return false;
    const status = (latestInvitation.status || "").toLowerCase();
    if (status === "revoked" || status === "completed") return false;
    if (latestInvitation.expires_at) {
      return new Date(latestInvitation.expires_at).getTime() > Date.now();
    }
    return true;
  })();

  // -------------------------------------------------------------------------
  // Action: Create & Send Invitation
  // -------------------------------------------------------------------------
  const handleCreateInvitation = async (e) => {
    if (e) e.preventDefault();
    if (!cycle) return;
    setActionLoading(true);
    setError(null);

    try {
      const data = await inviteCandidateCvCycle(cycle.cycle_id, {
        notes: inviteNotes.trim() || undefined,
        validity_hours: Number(validityHours) || 72,
      });

      setGeneratedInviteLink(data.invitation_url);
      setSuccessNotice("Invitation générée avec succès.");
      loadCycleData();
      if (onCycleUpdated) onCycleUpdated();
    } catch (err) {
      setError(getApiErrorMessage(err, "Impossible d'inviter le candidat"));
      logAppError("CvCycleWorkflowPanel.createInvitation", err);
    } finally {
      setActionLoading(false);
    }
  };

  // -------------------------------------------------------------------------
  // Action: Revoke Invitation
  // -------------------------------------------------------------------------
  const handleRevokeInvitation = async () => {
    if (!invitationToRevoke) return;
    setActionLoading(true);
    setError(null);

    try {
      await apiClient.post(
        `/recruitment/cv-invitations/${invitationToRevoke.invitation_id}/revoke`,
        { reason: revokeReason.trim() || "Révocation interne" }
      );

      setShowRevokeModal(false);
      setInvitationToRevoke(null);
      setRevokeReason("");
      setSuccessNotice("Accès candidat révoqué avec succès. Verrou libéré.");
      loadCycleData();
      if (onCycleUpdated) onCycleUpdated();
    } catch (err) {
      setError(getApiErrorMessage(err, "Impossible de révoquer l'invitation"));
      logAppError("CvCycleWorkflowPanel.revokeInvitation", err);
    } finally {
      setActionLoading(false);
    }
  };

  // -------------------------------------------------------------------------
  // Action: Finalize Cycle
  // -------------------------------------------------------------------------
  const handleFinalizeCycle = async () => {
    if (!cycle || !finalDocId) return;
    setActionLoading(true);
    setError(null);

    try {
      await apiClient.post(
        `/recruitment/cv-cycles/${cycle.cycle_id}/finalize`,
        {
          final_document_id: finalDocId.trim(),
          expected_revision: cycle.revision || 1,
        }
      );

      setShowFinalizeModal(false);
      setSuccessNotice("Cycle CV finalisé avec succès. Document immuable.");
      loadCycleData();
      if (onCycleUpdated) onCycleUpdated();
    } catch (err) {
      setError(getApiErrorMessage(err, "Impossible de finaliser le cycle"));
      logAppError("CvCycleWorkflowPanel.finalizeCycle", err);
    } finally {
      setActionLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="p-6 rounded-2xl bg-[#14183a]/80 border border-slate-800 flex items-center justify-center">
        <Loader2 className="w-5 h-5 animate-spin text-violet-400" />
      </div>
    );
  }

  if (!cycle) {
    return (
      <div className="p-6 rounded-2xl bg-[#14183a]/80 border border-slate-800 text-center">
        <FileText className="w-8 h-8 mx-auto text-slate-500 mb-2" />
        <p className="text-sm text-slate-300">Aucun cycle de préparation CV en cours.</p>
      </div>
    );
  }

  return (
    <div className="bg-[#14183a]/90 border border-slate-700/60 rounded-2xl p-6 shadow-xl space-y-6">
      {/* Header: Title, Status Pill, Revision */}
      <div className="flex flex-wrap items-center justify-between gap-4 border-b border-slate-800 pb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-violet-600/20 border border-violet-500/30 flex items-center justify-center text-violet-400">
            <FileText className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-base font-bold text-white flex items-center gap-2">
              <span>Cycle CV Lenvia</span>
              <span className="text-xs px-2 py-0.5 rounded-full bg-slate-800 text-slate-300 border border-slate-700 font-mono">
                Rév. {cycle.revision || 1}
              </span>
            </h2>
            <p className="text-xs text-slate-400">ID : {cycle.cycle_id}</p>
          </div>
        </div>

        {/* Status Pill */}
        <div className="flex items-center gap-2">
          {cycle.status === "draft" && (
            <span className="px-3 py-1 rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-300 text-xs font-semibold">
              Brouillon
            </span>
          )}
          {cycle.status === "in_review" && (
            <span className="px-3 py-1 rounded-full bg-blue-500/10 border border-blue-500/30 text-blue-300 text-xs font-semibold">
              En revue
            </span>
          )}
          {cycle.status === "finalized" && (
            <span className="px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-xs font-semibold flex items-center gap-1">
              <CheckCircle2 className="w-3.5 h-3.5" />
              Finalisé (Immuable)
            </span>
          )}
          {cycle.status === "archived" && (
            <span className="px-3 py-1 rounded-full bg-slate-700/40 border border-slate-600 text-slate-400 text-xs">
              Archivé
            </span>
          )}
        </div>
      </div>

      {/* Notifications */}
      {error && (
        <div className="p-3.5 rounded-xl bg-red-500/10 border border-red-500/30 text-red-300 text-xs flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}
      {successNotice && (
        <div className="p-3.5 rounded-xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-xs flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
          <span>{successNotice}</span>
        </div>
      )}

      {/* Candidate Invitation Status Card */}
      <div className="p-4 rounded-xl bg-slate-900/60 border border-slate-800 space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-xs font-semibold text-slate-300">
            <UserCheck className="w-4 h-4 text-violet-400" />
            <span>Collaboration Candidat</span>
          </div>
          {latestInvitation ? (
            <span className="text-xs px-2.5 py-0.5 rounded-full bg-slate-800 text-slate-300 border border-slate-700 capitalize">
              {latestInvitation.status}
            </span>
          ) : (
            <span className="text-xs text-slate-500">Aucune invitation active</span>
          )}
        </div>

        {/* Active lock alert */}
        {isCandidateLockActive ? (
          <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-300 text-xs flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Lock className="w-4 h-4 animate-pulse text-amber-400" />
              <span>
                <strong>Édition candidat en cours :</strong> verrou d'activité actif (inactivité max 10 min).
              </span>
            </div>
            {latestInvitation && (
              <button
                onClick={() => {
                  setInvitationToRevoke(latestInvitation);
                  setShowRevokeModal(true);
                }}
                className="px-2.5 py-1 rounded-lg bg-red-600/20 hover:bg-red-600/30 border border-red-500/30 text-red-300 text-xs font-medium"
              >
                Révoquer
              </button>
            )}
          </div>
        ) : isInvitationActive ? (
          <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/30 text-blue-300 text-xs flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-blue-400" />
              <span>Invitation valide jusqu'au {new Date(latestInvitation.expires_at).toLocaleString("fr-FR")}.</span>
            </div>
            <button
              onClick={() => {
                setInvitationToRevoke(latestInvitation);
                setShowRevokeModal(true);
              }}
              className="px-2.5 py-1 rounded-lg bg-red-600/20 hover:bg-red-600/30 border border-red-500/30 text-red-300 text-xs font-medium"
            >
              Révoquer
            </button>
          </div>
        ) : (
          <div className="flex items-center justify-between text-xs text-slate-400">
            <span>Le candidat n'a pas accès en écriture.</span>
            {cycle.status !== "finalized" && canWrite && (
              <button
                onClick={() => setShowInviteModal(true)}
                className="px-3 py-1.5 rounded-lg bg-violet-600 hover:bg-violet-500 text-white font-medium text-xs flex items-center gap-1.5 transition-all"
              >
                <Send className="w-3.5 h-3.5" />
                <span>Inviter le candidat</span>
              </button>
            )}
          </div>
        )}
      </div>

      {/* Primary Action Button (Single, non-conflicting) */}
      <div className="pt-2">
        {cycle.status === "finalized" ? (
          <div className="p-4 rounded-xl bg-emerald-500/5 border border-emerald-500/20 flex items-center justify-between">
            <div>
              <div className="text-sm font-semibold text-white flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                <span>Document finalisé et certifié</span>
              </div>
              <p className="text-xs text-slate-400 mt-0.5">
                Empreinte SHA-256 :{" "}
                <span className="font-mono text-slate-300">{cycle.final_document_sha256?.substring(0, 16)}...</span>
              </p>
            </div>
            <button
              onClick={() => setShowReuseModal(true)}
              className="px-4 py-2 rounded-xl bg-violet-600 hover:bg-violet-500 text-white text-xs font-medium flex items-center gap-1.5 transition-all shadow-md shadow-violet-600/20"
            >
              <Share2 className="w-3.5 h-3.5" />
              <span>Réutiliser pour un besoin</span>
            </button>
          </div>
        ) : isCandidateLockActive ? (
          <button
            disabled
            className="w-full py-3 rounded-xl bg-slate-800 text-slate-400 text-xs font-medium flex items-center justify-center gap-2 cursor-not-allowed opacity-80"
          >
            <Lock className="w-4 h-4" />
            <span>Édition candidat active — Finalisation bloquée</span>
          </button>
        ) : isInvitationActive ? (
          <button
            disabled
            title="Révoquez l'invitation ou attendez que le candidat termine avant de finaliser."
            className="w-full py-3 rounded-xl bg-slate-800 text-slate-400 text-xs font-medium flex items-center justify-center gap-2 cursor-not-allowed opacity-80"
          >
            <Clock className="w-4 h-4" />
            <span>Invitation en cours — Attente de validation candidat</span>
          </button>
        ) : (
          <button
            onClick={() => setShowFinalizeModal(true)}
            disabled={!canWrite}
            className="w-full py-3 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white text-xs font-bold flex items-center justify-center gap-2 transition-all shadow-lg shadow-emerald-600/20"
          >
            <CheckCircle2 className="w-4 h-4" />
            <span>Finaliser le CV (Immuable)</span>
          </button>
        )}
      </div>

      {/* 3 Latest Finalized Versions History */}
      {cyclesHistory.length > 0 && (
        <div className="border-t border-slate-800 pt-4 space-y-3">
          <div className="flex items-center justify-between text-xs font-semibold text-slate-300">
            <span className="flex items-center gap-1.5">
              <History className="w-4 h-4 text-violet-400" />
              <span>Historique des versions certifiées (Rétention L4)</span>
            </span>
            <span className="text-slate-500">3 dernières versions</span>
          </div>

          <div className="space-y-2">
            {cyclesHistory
              .filter((c) => c.status === "finalized")
              .slice(0, 3)
              .map((hist, idx) => (
                <div
                  key={hist.cycle_id}
                  className="p-3 rounded-xl bg-slate-900/60 border border-slate-800 flex items-center justify-between text-xs"
                >
                  <div>
                    <span className="font-semibold text-white">Version #{idx + 1}</span>
                    <span className="text-slate-500 ml-2">
                      {hist.finalized_at ? new Date(hist.finalized_at).toLocaleDateString("fr-FR") : "Date inconnue"}
                    </span>
                    <div className="text-[10px] text-slate-400 font-mono mt-0.5">
                      SHA: {hist.final_document_sha256 ? `${hist.final_document_sha256.substring(0, 12)}...` : "N/A"}
                    </div>
                  </div>
                  <span className="px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-300 border border-emerald-500/30 text-[10px]">
                    Certifié
                  </span>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Invite Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
          <div className="max-w-md w-full bg-[#14183a] border border-slate-700 rounded-2xl p-6 shadow-2xl space-y-4">
            <h3 className="text-lg font-bold text-white flex items-center gap-2">
              <Send className="w-5 h-5 text-violet-400" />
              <span>Inviter le candidat à éditer son CV</span>
            </h3>
            <div className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-300 text-xs flex items-start gap-2.5">
              <ShieldAlert className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
              <div className="space-y-1">
                <span className="font-semibold block">Garde fail-closed en production</span>
                <p className="text-[11px] text-amber-300/80 leading-relaxed">
                  Le portail candidat est actuellement désactivé en environnement de production (HTTP 503). La génération de jeton est fonctionnelle pour les tests et la qualification interne.
                </p>
              </div>
            </div>
            <p className="text-xs text-slate-300 leading-relaxed">
              Le candidat recevra un lien sécurisé lui permettant d'accéder à son document via un code OTP à 6 chiffres.
            </p>

            {generatedInviteLink ? (
              <div className="space-y-3 pt-2">
                <div className="p-3 rounded-xl bg-slate-900 border border-slate-700 text-xs font-mono text-violet-300 break-all">
                  {generatedInviteLink}
                </div>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(generatedInviteLink);
                    setSuccessNotice("Lien copié dans le presse-papier !");
                    setShowInviteModal(false);
                    setGeneratedInviteLink(null);
                  }}
                  className="w-full py-2.5 rounded-xl bg-violet-600 hover:bg-violet-500 text-white text-xs font-medium"
                >
                  Copier le lien d'invitation
                </button>
              </div>
            ) : (
              <form onSubmit={handleCreateInvitation} className="space-y-4">
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Durée de validité (heures)</label>
                  <input
                    type="number"
                    min={1}
                    max={168}
                    value={validityHours}
                    onChange={(e) => setValidityHours(e.target.value)}
                    className="w-full px-3 py-2 rounded-xl bg-slate-900 border border-slate-700 text-white text-xs"
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Note interne (optionnelle)</label>
                  <textarea
                    rows={2}
                    value={inviteNotes}
                    onChange={(e) => setInviteNotes(e.target.value)}
                    placeholder="Ex: Invitation suite entretien téléphonique..."
                    className="w-full px-3 py-2 rounded-xl bg-slate-900 border border-slate-700 text-white text-xs"
                  />
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <button
                    type="button"
                    onClick={() => setShowInviteModal(false)}
                    className="px-4 py-2 rounded-xl bg-slate-800 text-slate-300 text-xs font-medium"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    disabled={actionLoading}
                    className="px-4 py-2 rounded-xl bg-violet-600 hover:bg-violet-500 text-white text-xs font-medium flex items-center gap-1.5"
                  >
                    {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    <span>Générer l'invitation</span>
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Revoke Modal */}
      {showRevokeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
          <div className="max-w-md w-full bg-[#14183a] border border-red-500/30 rounded-2xl p-6 shadow-2xl space-y-4">
            <h3 className="text-lg font-bold text-white flex items-center gap-2">
              <ShieldAlert className="w-5 h-5 text-red-400" />
              <span>Révoquer l'accès candidat</span>
            </h3>
            <p className="text-xs text-slate-300 leading-relaxed">
              Cette action invalide immédiatement toutes les sessions actives du candidat et libère le verrou d'édition.
            </p>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Motif de révocation</label>
              <input
                type="text"
                value={revokeReason}
                onChange={(e) => setRevokeReason(e.target.value)}
                placeholder="Ex: Reprise interne urgente, fin de délai..."
                className="w-full px-3 py-2 rounded-xl bg-slate-900 border border-slate-700 text-white text-xs"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button
                onClick={() => setShowRevokeModal(false)}
                className="px-4 py-2 rounded-xl bg-slate-800 text-slate-300 text-xs font-medium"
              >
                Annuler
              </button>
              <button
                onClick={handleRevokeInvitation}
                disabled={actionLoading}
                className="px-4 py-2 rounded-xl bg-red-600 hover:bg-red-500 text-white text-xs font-medium flex items-center gap-1.5"
              >
                {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                <span>Confirmer la révocation</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Finalize Modal */}
      {showFinalizeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
          <div className="max-w-md w-full bg-[#14183a] border border-emerald-500/30 rounded-2xl p-6 shadow-2xl space-y-4">
            <h3 className="text-lg font-bold text-white flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-emerald-400" />
              <span>Finaliser le cycle CV</span>
            </h3>
            <p className="text-xs text-slate-300 leading-relaxed">
              La finalisation fige le document de façon permanente. Assurez-vous d'avoir renseigné l'ID du document persistant.
            </p>
            <div>
              <label className="block text-xs text-slate-400 mb-1">ID Document Persistant (Stockage Durable)</label>
              <input
                type="text"
                value={finalDocId}
                onChange={(e) => setFinalDocId(e.target.value)}
                placeholder="doc_..."
                className="w-full px-3 py-2 rounded-xl bg-slate-900 border border-slate-700 text-white text-xs font-mono"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button
                onClick={() => setShowFinalizeModal(false)}
                className="px-4 py-2 rounded-xl bg-slate-800 text-slate-300 text-xs font-medium"
              >
                Annuler
              </button>
              <button
                onClick={handleFinalizeCycle}
                disabled={actionLoading || !finalDocId.trim()}
                className="px-4 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-medium flex items-center gap-1.5 disabled:opacity-50"
              >
                {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                <span>Confirmer la finalisation</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
